import { useEffect, useMemo } from "react";
import {
  SimpleFormIterator,
  ArrayInput,
  TextInput,
  NumberInput,
  SelectInput,
  ImageInput,
  BooleanInput,
  required,
  RichTextField,
  TextField,
  minValue,
  maxValue,
} from "react-admin";
import { useFormContext } from "react-hook-form";
import { Alert } from "@mui/material";

import MarkdownInput from "../common/MarkdownInput";
import { formatImage } from "../../utils/InputUtils";
import PreviewImage from "../common/PreviewImage";

const MarkdownForm = ({ rest, isImageRequired }) => (
  <>
    <ImageInput
      source="image_url"
      label="Image"
      accept="image/*"
      format={formatImage}
      validate={isImageRequired ? [required()] : []}
      placeholder={<p>Drop your file here</p>}
    >
      <PreviewImage source="src" />
    </ImageInput>
    <MarkdownInput
      label="Markdown"
      validate={required()}
      isRequired
      source="content_item_json_i18n_en.markdown_i18n"
      {...rest}
    />
  </>
);

const MarkdownESForm = ({ rest }) => (
  <MarkdownInput
    label="Markdown (Spanish)"
    source="content_item_json_i18n_es.markdown_i18n"
    {...rest}
  />
);

const ImageHotspotsForm = ({ rest }) => (
  <>
    {MarkdownForm({ rest, isImageRequired: true })}
    <ArrayInput
      source="content_item_json_i18n_en.hotspots"
      validate={required()}
      label="Hotspots"
    >
      <SimpleFormIterator>
        <NumberInput label="id" source="id" validate={required()} {...rest} />
        <NumberInput
          label="X axis in %"
          source="x"
          max="99"
          min="1"
          validate={[
            required(),
            minValue(1, "X axis in % should be more than 0"),
            maxValue(99, "X axis in % should be less than 100"),
          ]}
          {...rest}
        />
        <NumberInput
          label="Y axis in %"
          source="y"
          max="99"
          min="1"
          validate={[
            required(),
            minValue(1, "Y axis in % should be more than 0"),
            maxValue(99, "Y axis in % should be less than 100"),
          ]}
          {...rest}
        />
        <NumberInput
          label="Highlighted area width in %"
          source="percentWidth"
          validate={[
            required(),
            minValue(
              0,
              "Highlighted area width in % should be more than or equal to 0"
            ),
            maxValue(
              100,
              "Highlighted area width in % should be less than or equal to 100"
            ),
          ]}
          {...rest}
        />
        <NumberInput
          label="Highlighted area height in %"
          source="percentHeight"
          validate={[
            required(),
            minValue(
              0,
              "Highlighted area height in % should be more than or equal to 0"
            ),
            maxValue(
              100,
              "Highlighted area height in % should be less than or equal to 100"
            ),
          ]}
          {...rest}
        />
        <MarkdownInput
          label="Markdown"
          addLabel={false}
          source="markdown_i18n"
          validate={required()}
          {...rest}
        />
      </SimpleFormIterator>
    </ArrayInput>
  </>
);

const ImageHotspotsESForm = ({ rest }) => (
  <>
    {MarkdownESForm({ rest, isImageRequired: true })}
    <Alert severity="warning">
      &lowast;Please ensure you add Spanish hotspots in the same amount and
      order as added for English
    </Alert>
    <ArrayInput
      source="content_item_json_i18n_es.hotspots"
      validate={required()}
      label="Hotspots (Spanish)"
    >
      <SimpleFormIterator>
        <NumberInput
          label="id (Spanish)"
          source="id"
          validate={required()}
          {...rest}
        />
        <NumberInput
          label="X axis in % (Spanish)"
          source="x"
          max="99"
          min="1"
          validate={required()}
          {...rest}
        />
        <NumberInput
          label="Y axis in % (Spanish)"
          source="y"
          max="99"
          validate={required()}
          min="1"
          {...rest}
        />
        <NumberInput
          label="Highlighted area width in % (Spanish)"
          source="percentWidth"
          max="100"
          min="0"
          {...rest}
        />
        <NumberInput
          label="Highlighted area height in % (Spanish)"
          source="percentHeight"
          max="100"
          min="0"
          {...rest}
        />
        <MarkdownInput
          label="Markdown (Spanish)"
          addLabel={false}
          source="markdown_i18n"
          validate={required()}
          {...rest}
        />
      </SimpleFormIterator>
    </ArrayInput>
  </>
);

const MultipleChoiceQuestionWithCorrectAnswerForm = ({ rest, formData }) => {
  const { content_item_json_i18n_en: contentItemJson } = formData;
  const getNextAnswerId = useMemo(() => {
    const answers = contentItemJson?.answers || [];
    return answers.length;
  }, [contentItemJson?.answers]);

  const { formState, clearErrors } = useFormContext();

  useEffect(() => {
    if (formState.isValid) {
      clearErrors();
    }
  }, [formState.isValid]);

  return (
    <>
      <TextInput
        label="Question"
        source="content_item_json_i18n_en.question.question_text_i18n"
        validate={required()}
        multiline
        {...rest}
      />
      <BooleanInput
        label="Response is required"
        source="content_item_json_i18n_en.question.response_is_required"
        InputProps={{ disabled: true }}
        defaultValue
      />
      <ArrayInput
        source="content_item_json_i18n_en.answers"
        validate={required()}
        label="Answers"
      >
        <SimpleFormIterator>
          <NumberInput
            label="Answer Id"
            helperText="Unique ids"
            source="answer_id"
            defaultValue={getNextAnswerId}
            validate={required()}
            InputProps={{ disabled: true }}
            {...rest}
          />
          <TextInput
            label="Answer Text"
            source="answer_text_i18n"
            multiline
            validate={required()}
            {...rest}
          />
          <TextInput
            label="Answer Explanation"
            source="answer_explanation_i18n"
            validate={required()}
            multiline
            {...rest}
          />
          <SelectInput
            label="Additional Comments"
            source="additional_comments"
            choices={[{ id: "NONE", name: "NONE" }]}
            defaultValue="NONE"
            {...rest}
          />
          <BooleanInput
            label="Is Correct Answer"
            source="is_correct_answer"
            helperText="Select only one correct answer"
            defaultValue={false}
          />
        </SimpleFormIterator>
      </ArrayInput>
    </>
  );
};

const MultipleChoiceQuestionWithCorrectAnswerESForm = ({ rest, formData }) => {
  const { content_item_json_i18n_es: contentItemJson } = formData;
  const getNextAnswerId = useMemo(() => {
    const answers = contentItemJson?.answers || [];
    return answers.length;
  }, [contentItemJson?.answers]);
  return (
    <>
      <TextInput
        label="Question (Spanish)"
        source="content_item_json_i18n_es.question.question_text_i18n"
        validate={required()}
        multiline
        {...rest}
      />
      <Alert severity="warning">
        Please ensure you add Spanish answers in the same amount and order as
        English answers
      </Alert>
      <ArrayInput
        source="content_item_json_i18n_es.answers"
        validate={required()}
        label="Answers (Spanish)"
      >
        <SimpleFormIterator>
          <NumberInput
            label="Answer Id (Spanish)"
            helperText="Unique ids"
            source="answer_id"
            defaultValue={getNextAnswerId}
            validate={required()}
            InputProps={{ disabled: true }}
            {...rest}
          />
          <TextInput
            label="Answer Text (Spanish)"
            source="answer_text_i18n"
            multiline
            validate={required()}
            {...rest}
          />
          <TextInput
            label="Answer Explanation (Spanish)"
            source="answer_explanation_i18n"
            validate={required()}
            multiline
            {...rest}
          />
          <SelectInput
            label="Additional Comments (Spanish)"
            source="additional_comments"
            choices={[{ id: "NONE", name: "NONE" }]}
            defaultValue="NONE"
            {...rest}
          />
          <BooleanInput
            label="Is Correct Answer (Spanish)"
            source="is_correct_answer"
            helperText="Select only one correct answer"
            defaultValue={false}
          />
        </SimpleFormIterator>
      </ArrayInput>
    </>
  );
};

const QuestionWithoutCorrectAnswerForm = ({ rest, formData }) => {
  const { content_item_json_i18n_en: contentItemJson } = formData;
  const getNextAnswerId = useMemo(() => {
    const answers = contentItemJson?.answers || [];
    return answers.length;
  }, [contentItemJson?.answers]);
  return (
    <>
      <TextInput
        label="Question"
        source="content_item_json_i18n_en.question.question_text_i18n"
        validate={required()}
        multiline
        {...rest}
      />
      <BooleanInput
        label="Response is required"
        source="content_item_json_i18n_en.question.response_is_required"
        defaultValue
      />
      <ArrayInput
        source="content_item_json_i18n_en.answers"
        validate={required()}
        label="Answers"
      >
        <SimpleFormIterator>
          <NumberInput
            label="Answer Id"
            helperText="Unique ids"
            source="answer_id"
            defaultValue={getNextAnswerId}
            validate={required()}
            InputProps={{ disabled: true }}
            {...rest}
          />
          <TextInput
            label="Answer Text"
            source="answer_text_i18n"
            multiline
            validate={required()}
            {...rest}
          />
          <SelectInput
            label="Additional Comments"
            source="additional_comments"
            choices={[
              { id: "NONE", name: "NONE" },
              { id: "OPTIONAL", name: "OPTIONAL" },
              { id: "REQUIRED", name: "REQUIRED" },
            ]}
            defaultValue="NONE"
            {...rest}
          />
        </SimpleFormIterator>
      </ArrayInput>
    </>
  );
};

const QuestionWithoutCorrectAnswerESForm = ({ rest, formData }) => {
  const { content_item_json_i18n_es: contentItemJson } = formData;
  const getNextAnswerId = useMemo(() => {
    const answers = contentItemJson?.answers || [];
    return answers.length;
  }, [contentItemJson?.answers]);
  return (
    <>
      <TextInput
        label="Question (Spanish)"
        source="content_item_json_i18n_es.question.question_text_i18n"
        validate={required()}
        multiline
        {...rest}
      />
      <Alert severity="warning">
        Please ensure you add Spanish answers in the same amount and order as
        English answers
      </Alert>
      <ArrayInput
        source="content_item_json_i18n_es.answers"
        label="Answers (Spanish)"
        validate={required()}
      >
        <SimpleFormIterator>
          <NumberInput
            label="Answer Id (Spanish)"
            helperText="Unique ids"
            source="answer_id"
            defaultValue={getNextAnswerId}
            validate={required()}
            InputProps={{ disabled: true }}
            {...rest}
          />
          <TextInput
            label="Answer Text (Spanish)"
            source="answer_text_i18n"
            multiline
            validate={required()}
            {...rest}
          />
          <SelectInput
            label="Additional Comments (Spanish)"
            source="additional_comments"
            choices={[
              { id: "NONE", name: "NONE" },
              { id: "OPTIONAL", name: "OPTIONAL" },
              { id: "REQUIRED", name: "REQUIRED" },
            ]}
            defaultValue="NONE"
            {...rest}
          />
        </SimpleFormIterator>
      </ArrayInput>
    </>
  );
};

const FreeTextWithoutCorrectAnswerForm = () => (
  <>
    <BooleanInput
      label="Response is required"
      source="content_item_json_i18n_en.question.response_is_required"
      defaultValue
    />
    <SelectInput
      source="content_item_json_i18n_en.question.response_length"
      label="Response Length"
      validate={required()}
      choices={[
        { id: "SHORT", name: "SHORT" },
        { id: "LONG", name: "LONG" },
      ]}
    />
    <TextInput
      label="Placeholder Text"
      source="content_item_json_i18n_en.question.placeholder_text_i18n"
      validate={required()}
      multiline
      fullWidth
    />
    <TextInput
      label="Question"
      source="content_item_json_i18n_en.question.question_text_i18n"
      validate={required()}
      multiline
      fullWidth
    />
  </>
);

const FreeTextWithoutCorrectAnswerESForm = () => (
  <>
    <SelectInput
      source="content_item_json_i18n_es.question.response_length"
      label="Response Length (Spanish)"
      validate={required()}
      choices={[
        { id: "SHORT", name: "SHORT" },
        { id: "LONG", name: "LONG" },
      ]}
    />
    <TextInput
      label="Placeholder Text (Spanish)"
      source="content_item_json_i18n_es.question.placeholder_text_i18n"
      validate={required()}
      multiline
      fullWidth
    />
    <TextInput
      label="Question"
      source="content_item_json_i18n_es.question.question_text_i18n"
      validate={required()}
      multiline
      fullWidth
    />
  </>
);

const NumericWithoutCorrectAnswerForm = () => (
  <>
    <BooleanInput
      label="Response is required"
      source="content_item_json_i18n_en.question.response_is_required"
      defaultValue
    />
    <NumberInput
      label="Decimal Precision"
      source="content_item_json_i18n_en.question.decimal_precision"
      fullWidth
      defaultValue={0}
    />
    <NumberInput
      label="Validation Max Value"
      source="content_item_json_i18n_en.question.validation_max_value"
      fullWidth
      defaultValue={null}
    />
    <NumberInput
      label="Validation Min Value"
      source="content_item_json_i18n_en.question.validation_min_value"
      fullWidth
      defaultValue={null}
    />
    <TextInput
      label="Pre Qualifier"
      source="content_item_json_i18n_en.question.pre_qualifier"
      defaultValue=""
      fullWidth
    />
    <TextInput
      label="Post Qualifier"
      source="content_item_json_i18n_en.question.post_qualifier"
      defaultValue=""
      fullWidth
    />
    <TextInput
      label="Question"
      source="content_item_json_i18n_en.question.question_text_i18n"
      validate={required()}
      multiline
      fullWidth
    />
  </>
);

const NumericWithoutCorrectAnswerESForm = () => (
  <TextInput
    label="Question (Spanish)"
    source="content_item_json_i18n_es.question.question_text_i18n"
    validate={required()}
    multiline
    fullWidth
  />
);

const TutorialSlideForm = ({ rest }) => (
  <>
    <ImageInput
      source="image_url"
      label="Image"
      accept="image/*"
      format={formatImage}
      validate={required()}
      placeholder={<p>Drop your file here</p>}
    >
      <PreviewImage source="src" />
    </ImageInput>
    <TextInput
      label="Slide"
      validate={required()}
      source="content_item_json_i18n_en.slide_text"
      multiline
      {...rest}
    />
  </>
);

const TutorialSlideESForm = ({ rest }) => (
  <TextInput
    label="Slide (Spanish)"
    validate={required()}
    source="content_item_json_i18n_es.slide_text"
    multiline
    {...rest}
  />
);

const markdownPreview = props => (
  <RichTextField
    source="content_item_json_i18n_en.markdown_i18n"
    stripTags
    {...props}
  />
);

const markdownESPreview = props => (
  <RichTextField
    source="content_item_json_i18n_es.markdown_i18n"
    stripTags
    {...props}
  />
);

const tutorialPreview = props => (
  <TextField source="content_item_json_i18n_en.slide_text" {...props} />
);

const tutorialESPreview = props => (
  <TextField source="content_item_json_i18n_es.slide_text" {...props} />
);

const questionPreview = props => (
  <TextField
    source="content_item_json_i18n_en.question.question_text_i18n"
    {...props}
  />
);

const questionESPreview = props => (
  <TextField
    source="content_item_json_i18n_es.question.question_text_i18n"
    {...props}
  />
);

export {
  MarkdownForm,
  MarkdownESForm,
  ImageHotspotsForm,
  ImageHotspotsESForm,
  TutorialSlideForm,
  TutorialSlideESForm,
  MultipleChoiceQuestionWithCorrectAnswerForm,
  MultipleChoiceQuestionWithCorrectAnswerESForm,
  QuestionWithoutCorrectAnswerForm,
  QuestionWithoutCorrectAnswerESForm,
  FreeTextWithoutCorrectAnswerForm,
  FreeTextWithoutCorrectAnswerESForm,
  NumericWithoutCorrectAnswerForm,
  NumericWithoutCorrectAnswerESForm,
  markdownPreview,
  markdownESPreview,
  tutorialPreview,
  tutorialESPreview,
  questionPreview,
  questionESPreview,
};
